import React, { useState, useEffect } from "react";
import { parse, format, isValid } from "date-fns";
import FieldDescription from "../tools/FieldDescription";
import { DeathRelatedInfoType } from "../../static/types/commonTypes/deathRelatedInfoType";
import { validateRelatedInfo } from "../../utils/validation/deathRelatedInfoValidationSchema";
import PostalCodeField from "./PostalCodeField";
import { DateOfBirthAndCityType } from "../../static/types/commonTypes/dateOfBirthAndCity";

type DeathRelatedInfoProps = {
  checked: boolean;
  deathRelatedInfo: DeathRelatedInfoType;
  onChange: (data: Partial<DeathRelatedInfoType>) => void;
  onValidationChange: (isValid: boolean) => void;
};

const DeathRelatedInfo: React.FC<DeathRelatedInfoProps> = ({
  checked,
  deathRelatedInfo,
  onChange,
  onValidationChange,
}) => {
  const { errors, isValid: dataValidity } = validateRelatedInfo(deathRelatedInfo);

  const [selectedDate, setSelectedDate] = useState<string>("");

  useEffect(() => {
    onValidationChange(dataValidity);
  }, [dataValidity, onValidationChange]);

  useEffect(() => {
    if (deathRelatedInfo.dateOfDeath) {
      const parsed = parse(deathRelatedInfo.dateOfDeath, "dd/MM/yyyy", new Date());
      if (isValid(parsed)) {
        setSelectedDate(format(parsed, "yyyy-MM-dd"));
      }
    }
  }, [deathRelatedInfo.dateOfDeath]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      setSelectedDate("");
      onChange({ dateOfDeath: "" });
      return;
    }
    const parsed = parse(inputValue, "yyyy-MM-dd", new Date());
    if (!isValid(parsed)) {
      setSelectedDate("");
      onChange({ dateOfDeath: "" });
      return;
    }
    const formattedDate = format(parsed, "dd/MM/yyyy");
    setSelectedDate(inputValue);
    onChange({ dateOfDeath: formattedDate });
  };

  const handleAbroadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isAbroad = e.target.checked;
    onChange({
      abroad: isAbroad,
      postalCode: "",
      city: "", 
      country: isAbroad ? "" : deathRelatedInfo.country,
    });
  };
  
  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ country: e.target.value });
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onChange({ city: e.target.value });
  };

  const openDatePicker = (id: string) => {
    const input = document.getElementById(id) as HTMLInputElement;
    input?.showPicker?.();
  };

  return (
    <div className="container my-4">
      <div className="card-body">
        <div className="mb-3 row">
          <label htmlFor="dateOfDeath" className="col-sm-3 col-form-label">
            Date du décès <strong className="text-danger">*</strong>
          </label>
          <div className="col-sm-6">
            <input
              type="date"
              id="dateOfDeath"
              value={selectedDate}
              onChange={handleDateChange}
              onClick={() => openDatePicker("dateOfDeath")}
              className={`description-input form-control ${checked && errors.dateOfDeath ? "is-invalid" : ""}`}
              placeholder="JJ/MM/AAAA"
            />
            {checked && errors.dateOfDeath && (
              <div className="invalid-feedback">{errors.dateOfDeath}</div>
            )}
            <FieldDescription description="Format : JJ/MM/AAAA" />
          </div>
        </div>

        <div className="mb-3 row">
          <div className="col-sm-3 form-label">Décès à l'étranger</div>
          <div className="col-sm-6">
            <input
              type="checkbox"
              id="abroad"
              checked={deathRelatedInfo.abroad || false}
              onChange={handleAbroadChange}
              className="form-check-input"
            />
            <label htmlFor="abroad" className="form-check-label ms-2">
              Cochez si le décès est survenu à l'étranger
            </label>
          </div>
        </div>

        {deathRelatedInfo.abroad ? (
          <>
            <div className="mb-3 row">
              <label htmlFor="country" className="col-sm-3 col-form-label">
                Pays <strong className="text-danger">*</strong>
              </label>
              <div className="col-sm-6">
                <input
                  type="text"
                  id="country"
                  value={deathRelatedInfo.country || ""}
                  onChange={handleCountryChange}
                  className={`form-control ${checked && errors.country ? "is-invalid" : ""}`}
                  placeholder="Nom du pays"
                />
                {checked && errors.country && (
                  <div className="invalid-feedback">{errors.country}</div>
                )}
                <FieldDescription description="Indiquez le pays du décès" />
              </div>
            </div>
            <div className="mb-3 row">
              <label htmlFor="city" className="col-sm-3 col-form-label">
                Ville <strong className="text-danger">*</strong>
              </label>
              <div className="col-sm-6">
                <input
                  type="text"
                  id="city"
                  value={deathRelatedInfo.city || ""}
                  onChange={handleCityChange}
                  className={`form-control ${checked && errors.city ? "is-invalid" : ""}`}
                  placeholder="Nom de la ville"
                />
                {checked && errors.city && (
                  <div className="invalid-feedback">{errors.city}</div>
                )}
                <FieldDescription description="Saisissez le nom de la ville" />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="col-sm-12">
              <PostalCodeField
                checked={checked}
                errors={errors}
                postalCode={deathRelatedInfo.postalCode || ""}
                onChange={(partialData: Partial<DateOfBirthAndCityType>) =>
                  onChange({ ...deathRelatedInfo, ...partialData })
                }
              />
            </div>

            <div className="row mb-3">
              <label htmlFor="city" className="col-sm-2 col-form-label">
                Ville <strong className="text-danger">*</strong>
              </label>
              <div className="col-sm-4">
                <select
                  id="city"
                  className={`form-select ${checked && errors.city ? "is-invalid" : ""}`}
                  value={deathRelatedInfo.city || ""}
                  onChange={(event) => onChange({ city: event.target.value })}
                >
                  {deathRelatedInfo.cities?.map((city, idx) => (
                    <option key={idx} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
                {checked && errors.city && (
                  <div className="invalid-feedback">{errors.city}</div>
                )}
                <FieldDescription description="Sélectionnez la ville associée à votre code postal" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeathRelatedInfo;
